import { FC, useMemo, useState, useRef, ReactNode } from 'react'
import { useTranslations } from 'next-intl'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'
import clsx from 'clsx'
import { useMobileDetector } from '@hooks/use-mobile-detector'
import PdImage from '@components/pd-image/pd-image'
import SlidingTabContent from '@components/standard/sliding-tab-content'
import SlidingTabs from '@components/standard/pd-sliding-tabs'

import { IFlipMultipleCards, SectionElementType, SectionElementItemType, Theme, paddingTopClassMap, paddingBottomClassMap } from '@common/pd-meta-components-types'

const Card: FC<Partial<SectionElementItemType & { color?: string; boxClassName?: string; borderRadius?: number }>> = ({
  color,
  title,
  desc,
  mediaUrl = '',
  boxClassName,
  borderRadius,
}) => {
  const cardRef = useRef<HTMLDivElement>(null)
  const [activation, setActivation] = useState<boolean>(false)

  useGSAP(() => {
    // 定义翻转动画
    gsap.to(cardRef.current, {
      rotateY: activation ? 180 : 0, // 根据状态翻转
      duration: 0.8,
      ease: 'ease-in-out',
    })
  }, [activation])

  const handleFlip = () => {
    setActivation(!activation) // 切换翻转状态
  }

  return (
    <div
      className={clsx(
        boxClassName,
        'relative [perspective:1000px] select-none',
        'w-72 h-384px',
        'laptop:w-72 laptop:h-384px'
      )}
    >
      <div ref={cardRef} className={clsx('w-full h-full relative [transform-style:preserve-3d]')}>
        {/* 正面 */}
        <div
          className={clsx('w-full h-full absolute overflow-hidden [backface-visibility:hidden]')}
          style={borderRadius ? { borderRadius: `${borderRadius}px` } : {}}
        >
          <div
            className={clsx(
              'absolute top-0 left-0 z-10',
              'w-full p-16px',
              'whitespace-pre-wrap text-xl font-semibold',
              'text-fc-primary'
            )}
            style={{ color }}
          >
            {title}
          </div>
          <PdImage src={mediaUrl} enableMask={false} object="cover" width={'100%'} height={'100%'} />

          {!!desc && (
            <span
              className={clsx(
                'absolute rounded-3xl cursor-pointer transition',
                "bg-[url('/icons/icon_white_add.svg')] bg-no-repeat bg-black/60 backdrop-blur-lg bg-center",
                'w-8 h-8 bottom-16px right-16px'
              )}
              onClick={handleFlip}
            />
          )}
        </div>

        {/* 反面 初始翻转180度 */}
        <div
          className={clsx(
            'w-full h-full absolute overflow-hidden bg-container-secondary [transform:rotateY(180deg)] [backface-visibility:hidden]',
            'rounded-2xl',
            'p-16px'
          )}
        >
          <div className={clsx('w-full', 'whitespace-pre-wrap text-fc-primary text-xl font-semibold', 'mb-10px')}>
            {title}
          </div>

          <div className={clsx('text-fc-primary text-sm font-normal')}>{desc}</div>

          <span
            className={clsx(
              'absolute rounded-3xl cursor-pointer transition',
              "bg-[url('/icons/icon_white_add.svg')] bg-no-repeat bg-black/60 backdrop-blur-lg bg-center",
              'w-8 h-8 bottom-16px right-16px',
              'rotate-45'
            )}
            onClick={handleFlip}
          />
        </div>
      </div>
    </div>
  )
}

const FlipMultipleCards: FC<IFlipMultipleCards> = ({ dataList: dataArr, theme, paddingTop = 'px100', paddingBottom = 'px100' }) => {
  const t = useTranslations()
  const isMobileDevice = useMobileDetector()
  const [activeTabKey, setActiveTabKey] = useState<number>(0)

  const data = useMemo<SectionElementType>(() => {
    return dataArr[activeTabKey]
  }, [dataArr, activeTabKey])

  const slideContent = useMemo<ReactNode[]>(() => {
    return dataArr?.map((item, index) => (
      <SlidingTabContent data={item.items} key={index} navigationBoxClassName="absolute bottom-0">
        <Card borderRadius={item.borderRadius} />
      </SlidingTabContent>
    ))
  }, [dataArr])

  const handleActiveTabChange = (activeIndex: number) => {
    setActiveTabKey(activeIndex)
  }

  return (
    <div className={clsx('w-full overflow-hidden', `pd-theme-${theme} bg-container-primary`)}>
      <div
        className={clsx(
          'mx-auto overflow-visible w-full laptop:max-w-300',
          'h-fit box-border laptop:px-0',
          paddingTopClassMap[paddingTop],
          paddingBottomClassMap[paddingBottom]
        )}
      >
        <SlidingTabs
          childrenBoxClassName={clsx('px-6', 'laptop:px-0')}
          contentBoxClassName={clsx('h-444px box-border laptop:h-504px')}
          tabsBoxClassName={clsx('pb-32px laptop:pb-40px')}
          tabsContainerClassName="laptop:rounded-full"
          tabLabels={dataArr.length > 1 ? dataArr.map((item) => item.name) : []}
          activeTabChange={handleActiveTabChange}
        >
          {slideContent}
        </SlidingTabs>

        <div className={clsx('justify-between align-top', 'mt-32px px-6', 'laptop:flex laptop:mt-24px laptop:px-0')}>
          <div className={clsx('flex-1')}>
            <div className={clsx('font-semibold text-fc-primary', 'text-24', 'laptop:text-32 laptop:leading-40px')}>
              {data?.name}
            </div>
            <div
              className={clsx(
                'font-normal',
                'text-fc-secondary text-16 mt-12px',
                'laptop:text-fc-primary laptop:text-xl laptop:mt-8px'
              )}
            >
              {data?.subTitle}
            </div>
          </div>
          {!!data.desc && (
            <div
              className={clsx(
                'flex-1 flex flex-col',
                'font-normal',
                'mt-12px text-fc-secondary text-16',
                'laptop:mt-0 laptop:text-fc-primary laptop:text-sm'
              )}
            >
              {data.desc}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FlipMultipleCards
